/**
 * Pure focus-trap helpers. Kept DOM-agnostic in shape so they can be
 * unit-tested against a lightweight fake DOM without jsdom.
 */

export interface FocusableLike {
  focus: () => void;
  hasAttribute?: (n: string) => boolean;
  getAttribute?: (n: string) => string | null;
  tabIndex?: number;
  disabled?: boolean;
  offsetParent?: unknown;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "audio[controls]",
  "video[controls]",
  "iframe",
  "[contenteditable='true']",
].join(",");

/** Returns focusable elements inside `root`, in DOM order, filtering hidden ones. */
export function getFocusable(root: Element | null): HTMLElement[] {
  if (!root) return [];
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return nodes.filter((el) => {
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    if (el.tabIndex < 0) return false;
    // offsetParent === null means detached / display:none in most cases.
    if (el.offsetParent === null && el.getClientRects().length === 0) return false;
    return true;
  });
}

export type TrapTabResult = "noop" | "wrap-forward" | "wrap-backward";

/**
 * Handle a Tab / Shift+Tab keyboard event to wrap focus inside `focusables`.
 * Pure logic (works with any array of focus-like objects) so it's testable.
 * Returns what happened (for tests) and calls `preventDefault()` on wrap.
 */
export function trapTab(
  event: {
    key: string;
    shiftKey: boolean;
    target: EventTarget | null;
    preventDefault: () => void;
  },
  focusables: FocusableLike[],
): TrapTabResult {
  if (event.key !== "Tab") return "noop";
  if (focusables.length === 0) {
    event.preventDefault();
    return "noop";
  }
  if (focusables.length === 1) {
    event.preventDefault();
    focusables[0].focus();
    return "noop";
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = event.target as FocusableLike | null;

  if (event.shiftKey) {
    if (active === first || active == null) {
      event.preventDefault();
      last.focus();
      return "wrap-backward";
    }
    return "noop";
  }

  if (active === last) {
    event.preventDefault();
    first.focus();
    return "wrap-forward";
  }
  return "noop";
}
